import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';

// HTTP status codes that are non-actionable and should not be sent to Sentry.
// 422 is intentionally NOT in this list: in our API it indicates a celebrate/Joi
// validation failure, which almost always means a frontend bug (wrong shape, stale
// client, missing form validation) and is worth surfacing.
const IGNORED_STATUS_CODES = [0, 401, 403, 404];

@Injectable()
export class SentryHttpInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        // Skip non-actionable HTTP errors to reduce noise
        if (!IGNORED_STATUS_CODES.includes(error.status)) {
          const errorMessage = error.error?.error || error.error?.message || error.message || 'Unknown HTTP error';

          const sentryError = new Error(`HTTP ${error.status}: ${errorMessage}`);
          sentryError.name = `HttpError ${error.status}`;

          Sentry.captureException(sentryError, {
            tags: {
              http_status: error.status,
              http_method: req.method,
              error_type: 'http_error',
            },
            extra: {
              url: error.url,
              status: error.status,
              statusText: error.statusText,
              errorBody: error.error,
              requestUrl: req.urlWithParams,
              requestMethod: req.method,
            },
            fingerprint: ['http-error', String(error.status), req.method, req.urlWithParams.split('?')[0]],
          });
        }

        return throwError(() => error);
      }),
    );
  }
}
